import React, { useState, useEffect, useCallback, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import {
  makeStyles, Grid, Paper, Typography, IconButton,
  Tooltip, Chip, Button, Divider, CircularProgress,
  Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,
} from "@material-ui/core";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RefreshIcon from "@material-ui/icons/Refresh";
import ChatIcon from "@material-ui/icons/Chat";
import EventIcon from "@material-ui/icons/Event";
import PersonIcon from "@material-ui/icons/Person";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getSocket } from "../../services/socket-io";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  REASON_LABELS,
  SNOOZE_PRESETS,
  calcDueDate,
  resolvePending,
  updatePending,
} from "../../services/ticketPendingService";
import { i18n } from "../../translate/i18n";
import { getLocale } from "../../translate/localeHelper";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    height: "100%",
    overflowY: "auto",
    backgroundColor: theme.palette.background.default,
  },
  pageTitle: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(2),
  },
  // Métricas superiores
  metricsGrid: {
    marginBottom: theme.spacing(2),
  },
  metCard: {
    padding: theme.spacing(1.5),
    textAlign: "center",
    cursor: "pointer",
    transition: "box-shadow 0.15s",
    "&:hover": { boxShadow: theme.shadows[3] },
  },
  metCardActive: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  metNum: {
    fontSize: 28,
    fontWeight: 500,
    lineHeight: 1.2,
  },
  metLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  numRed: { color: "#E24B4A" },
  numAmber: { color: "#EF9F27" },
  numBlue: { color: "#378ADD" },
  numGray: { color: theme.palette.text.secondary },
  // Filtros
  filtersRow: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  filterControl: { minWidth: 160 },
  // Grupos
  groupSection: {
    marginBottom: theme.spacing(2),
  },
  groupHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(1),
    paddingBottom: 6,
    borderBottom: `2px solid ${theme.palette.divider}`,
  },
  groupDot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    flexShrink: 0,
  },
  groupTitle: {
    fontWeight: 500,
    fontSize: 15,
  },
  groupCount: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginLeft: 4,
  },
  emptyGroup: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    padding: theme.spacing(1, 0),
  },
  // Cards de pendiente
  card: {
    padding: theme.spacing(1.5),
    marginBottom: 6,
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    borderLeft: "3px solid transparent",
    transition: "box-shadow 0.15s",
    "&:hover": { boxShadow: theme.shadows[2] },
  },
  cardOverdue: { borderLeftColor: "#E24B4A" },
  cardToday: { borderLeftColor: "#EF9F27" },
  cardUpcoming: { borderLeftColor: "#378ADD" },
  cardNoDate: { borderLeftColor: "#888780" },
  // Avatar iniciales
  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 500,
    flexShrink: 0,
  },
  avRed: { backgroundColor: "#FCEBEB", color: "#A32D2D" },
  avAmber: { backgroundColor: "#FAEEDA", color: "#854F0B" },
  avBlue: { backgroundColor: "#E6F1FB", color: "#185FA5" },
  avGray: { backgroundColor: "#F1EFE8", color: "#5F5E5A" },
  // Contenido de la card
  cardBody: { flex: 1, minWidth: 0 },
  cardTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 8,
  },
  cardName: { fontWeight: 500, fontSize: 14 },
  cardPhone: { fontSize: 12, color: theme.palette.text.secondary },
  cardTime: { fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0 },
  timeRed: { color: "#A32D2D" },
  timeAmber: { color: "#854F0B" },
  timeBlue: { color: "#185FA5" },
  timeGray: { color: "#5F5E5A" },
  cardMeta: {
    display: "flex",
    gap: 5,
    marginTop: 4,
    flexWrap: "wrap",
    alignItems: "center",
  },
  chipReason: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: "#EEEDFE",
    color: "#3C3489",
    borderRadius: 4,
  },
  chipNew: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: "#FCEBEB",
    color: "#A32D2D",
    borderRadius: 4,
  },
  chipNoDate: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: "#F1EFE8",
    color: "#5F5E5A",
    borderRadius: 4,
  },
  chipNoAssign: {
    height: 18,
    fontSize: "0.65rem",
    backgroundColor: "#FAEEDA",
    color: "#854F0B",
    borderRadius: 4,
  },
  cardNote: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 4,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  },
  cardFooter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 8,
    gap: 8,
  },
  cardFooterLeft: { minWidth: 0 },
  cardAssigned: { fontSize: 11, color: theme.palette.text.secondary },
  cardLastAction: { fontSize: 11, color: theme.palette.text.hint },
  cardActions: { display: "flex", gap: 4, flexShrink: 0 },
  btnAction: { fontSize: 11, padding: "3px 8px", minWidth: 0 },
  btnChat: {
    fontSize: 11,
    padding: "3px 8px",
    minWidth: 0,
    backgroundColor: "#E6F1FB",
    color: "#185FA5",
    "&:hover": { backgroundColor: "#B5D4F4" },
  },
  // Loading / empty state
  loadingWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
  // Modal de reprogramar
  modalContent: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    paddingTop: 8,
    minWidth: 320,
  },
}));

// Iniciales del nombre de contacto
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Formato de tiempo relativo
const formatRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - Date.now();
  const absDiff = Math.abs(diff);
  const mins = Math.floor(absDiff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (diff > 0) {
    // Futuro
    if (mins < 60) return { text: `en ${mins}m`, cls: "timeAmber" };
    if (hours < 24) return { text: `en ${hours}h`, cls: "timeAmber" };
    return { text: `en ${days}d`, cls: "timeBlue" };
  } else {
    // Pasado (vencido)
    if (mins < 60) return { text: `hace ${mins}m`, cls: "timeRed" };
    if (hours < 24) return { text: `hace ${hours}h`, cls: "timeRed" };
    return { text: `hace ${days}d`, cls: "timeRed" };
  }
};

// Clasificar pending en grupo
const classifyPending = (pending) => {
  if (pending.status === "overdue") return "overdue";
  if (!pending.dueDate) return "nodate";
  const due = new Date(pending.dueDate);
  const now = new Date();
  const todayEnd = new Date(now);
  todayEnd.setHours(23, 59, 59, 999);
  const in7days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  if (due <= todayEnd) return "today";
  if (due <= in7days) return "upcoming";
  return "upcoming"; // más de 7 días igual en upcoming pero al final
};

const PendingDashboard = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, realUser } = useContext(AuthContext);

  const [pendings, setPendings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(Date.now());
  const [now, setNow] = useState(Date.now());

  // Filtros
  const [viewMode, setViewMode] = useState("mine"); // mine | team | all
  const [filterUser, setFilterUser] = useState("");
  const [filterReason, setFilterReason] = useState("");
  const [filterMetric, setFilterMetric] = useState(""); // "" | overdue | today | upcoming | nodate
  const [users, setUsers] = useState([]);

  // Modal reprogramar
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [reschedulePreset, setReschedulePreset] = useState("tomorrow_9");
  const [rescheduleCustomDate, setRescheduleCustomDate] = useState("");
  const [rescheduleShowCustom, setRescheduleShowCustom] = useState(false);
  const [rescheduleSaving, setRescheduleSaving] = useState(false);

  // Timer para countdown en tiempo real
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Cargar lista de usuarios (para filtro de admin)
  useEffect(() => {
    if (user.profile !== "user") {
      api.get("/users").then(({ data }) => {
        setUsers(data.users || data || []);
      }).catch(() => {});
    }
  }, [user.profile]);

  // Cargar pendientes
  const loadPendings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status: "pending,overdue", pageSize: 200 });
      if (viewMode === "mine") params.append("assignedToUserId", user.id);
      if (filterUser) params.append("assignedToUserId", filterUser);
      if (filterReason) params.append("reasonCode", filterReason);

      const { data } = await api.get(`/ticket-pendings?${params.toString()}`);
      const rows = data.rows || data || [];
      setPendings(rows);
      setLastRefresh(Date.now());
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [viewMode, filterUser, filterReason, user.id]);

  useEffect(() => {
    loadPendings();
  }, [loadPendings]);

  // Socket: escuchar eventos ticketPending para refrescar
  useEffect(() => {
    const socket = getSocket();
    // TicketsList y layout (etapa 1) también escuchan "ticketPending" sobre este
    // mismo singleton: el off() va POR REFERENCIA o los dejaría sordos.
    const handleTicketPending = () => {
      // Refrescar la lista cuando cambia cualquier pending
      loadPendings();
    };
    socket.on("ticketPending", handleTicketPending);
    // SINGLETON getSocket(): off() por referencia, NUNCA disconnect().
    return () => socket.off("ticketPending", handleTicketPending);
  }, [loadPendings]);

  // Resolver pendiente
  const handleResolve = async (pending) => {
    try {
      await resolvePending(pending.id, "manual_resolved");
      setPendings(prev => prev.filter(p => p.id !== pending.id));
      toast.success(i18n.t("pendingDashboard.pendienteResuelto"));
    } catch (err) {
      toastError(err);
    }
  };

  // Abrir modal de reprogramar
  const handleOpenReschedule = (pending) => {
    setRescheduleTarget(pending);
    setReschedulePreset("tomorrow_9");
    setRescheduleShowCustom(false);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    const localIso = new Date(tomorrow.getTime() - tomorrow.getTimezoneOffset() * 60000)
      .toISOString().slice(0, 16);
    setRescheduleCustomDate(localIso);
    setRescheduleOpen(true);
  };

  // Guardar reprogramación
  const handleSaveReschedule = async () => {
    if (!rescheduleTarget) return;
    setRescheduleSaving(true);
    try {
      let dueDate = null;
      if (rescheduleShowCustom && rescheduleCustomDate) {
        dueDate = new Date(rescheduleCustomDate).toISOString();
      } else {
        const preset = SNOOZE_PRESETS.find(p => p.key === reschedulePreset);
        dueDate = calcDueDate(preset);
      }
      const updated = await updatePending(rescheduleTarget.id, {
        dueDate,
        snoozePreset: rescheduleShowCustom ? "custom" : reschedulePreset,
        reasonCode: rescheduleTarget.reasonCode,
      });
      setPendings(prev => prev.map(p => p.id === updated.id ? updated : p));
      toast.success(i18n.t("pendingDashboard.pendienteReprogramado"));
      setRescheduleOpen(false);
    } catch (err) {
      toastError(err);
    } finally {
      setRescheduleSaving(false);
    }
  };

  // Tomar pendiente (asignarse a uno mismo)
  const handleTake = async (pending) => {
    try {
      const updated = await updatePending(pending.id, {
        // feat_299: tomar un pendiente lo asigna al admin real, nunca al
        // usuario que está "viendo como" (la trazabilidad sigue siendo suya).
        assignedToUserId: realUser?.id ?? user.id,
        reasonCode: pending.reasonCode,
      });
      setPendings(prev => prev.map(p => p.id === updated.id ? updated : p));
      toast.success(i18n.t("pendingDashboard.pendienteAsignado"));
    } catch (err) {
      toastError(err);
    }
  };

  // Ir al chat
  const handleGoToChat = (pending) => {
    if (pending.ticketId) {
      history.push(`/tickets/${pending.ticketId}`);
    }
  };

  // Clasificar y filtrar pendientes
  const classified = {
    overdue: [],
    today: [],
    upcoming: [],
    nodate: [],
  };

  pendings.forEach(p => {
    const group = classifyPending(p);
    classified[group].push(p);
  });

  // Ordenar dentro de cada grupo
  classified.overdue.sort((a, b) => new Date(a.dueDate || a.lastActionAt) - new Date(b.dueDate || b.lastActionAt));
  classified.today.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  classified.upcoming.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  classified.nodate.sort((a, b) => new Date(a.lastActionAt) - new Date(b.lastActionAt));

  // Métricas
  const mine = pendings.filter(p =>
    p.assignedToUserId === user.id || p.createdByUserId === user.id
  ).length;
  const metrics = {
    mine,
    overdue: classified.overdue.length,
    today: classified.today.length,
    upcoming: classified.upcoming.length,
    nodate: classified.nodate.length,
    total: pendings.length,
  };

  // Filtro por métrica (al hacer clic en tarjeta de métricas)
  const getFilteredGroups = () => {
    if (!filterMetric) return classified;
    return {
      overdue: filterMetric === "overdue" ? classified.overdue : [],
      today: filterMetric === "today" ? classified.today : [],
      upcoming: filterMetric === "upcoming" ? classified.upcoming : [],
      nodate: filterMetric === "nodate" ? classified.nodate : [],
    };
  };

  const filteredGroups = getFilteredGroups();

  // Render de una card de pendiente
  const renderCard = (pending) => {
    const group = classifyPending(pending);
    const contact = pending.ticket?.contact || {};
    const contactName = contact.name || contact.number || `Ticket #${pending.ticketId}`;
    const initials = getInitials(contactName);
    const timeInfo = pending.dueDate ? formatRelativeTime(pending.dueDate) : null;
    const isOverdue = pending.status === "overdue";
    const isAssignedToMe = pending.assignedToUserId === Number(user.id);
    const hasNoAssignee = !pending.assignedToUserId;

    const avatarClass = isOverdue ? classes.avRed
      : group === "today" ? classes.avAmber
      : group === "upcoming" ? classes.avBlue
      : classes.avGray;

    const cardClass = isOverdue ? classes.cardOverdue
      : group === "today" ? classes.cardToday
      : group === "upcoming" ? classes.cardUpcoming
      : classes.cardNoDate;

    const timeClass = isOverdue ? classes.timeRed
      : group === "today" ? classes.timeAmber
      : group === "upcoming" ? classes.timeBlue
      : classes.timeGray;

    const timeText = isOverdue
      ? (timeInfo ? `${i18n.t("pendingDashboard.vencidoText")} ${timeInfo.text}` : i18n.t("pendingDashboard.vencidoText"))
      : timeInfo ? (timeInfo.text.startsWith("en") ? timeInfo.text : `${i18n.t("pendingDashboard.venceText")} ${timeInfo.text}`) : i18n.t("pendingDashboard.sinFechaChip");

    // Formatear fecha hoy con hora
    const formatDueLabel = () => {
      if (!pending.dueDate) return i18n.t("pendingDashboard.sinFechaChip");
      const due = new Date(pending.dueDate);
      const todayEnd = new Date();
      todayEnd.setHours(23, 59, 59, 999);
      if (due <= todayEnd && !isOverdue) {
        return `${i18n.t("pendingDashboard.hoyText")} ${due.toLocaleTimeString(getLocale(), { hour: "2-digit", minute: "2-digit" })}`;
      }
      if (isOverdue) return timeText;
      return due.toLocaleDateString(getLocale(), { day: "2-digit", month: "2-digit" })
        + " " + due.toLocaleTimeString(getLocale(), { hour: "2-digit", minute: "2-digit" });
    };

    const assignedName = pending.assignedTo?.name || null;
    const createdByName = pending.createdBy?.name || null;
    const lastActionDate = pending.lastActionAt
      ? new Date(pending.lastActionAt).toLocaleString(getLocale(), {
          day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
        })
      : null;

    return (
      <Paper key={pending.id} className={`${classes.card} ${cardClass}`} variant="outlined">
        {/* Avatar */}
        <div className={`${classes.avatar} ${avatarClass}`}>{initials}</div>

        {/* Cuerpo */}
        <div className={classes.cardBody}>
          <div className={classes.cardTop}>
            <div>
              <Typography className={classes.cardName}>{contactName}</Typography>
              {contact.number && (
                <Typography className={classes.cardPhone}>{contact.number}</Typography>
              )}
            </div>
            <Typography className={`${classes.cardTime} ${timeClass}`}>
              {formatDueLabel()}
            </Typography>
          </div>

          {/* Chips */}
          <div className={classes.cardMeta}>
            <Chip
              label={REASON_LABELS[pending.reasonCode] || pending.reasonCode}
              size="small"
              className={classes.chipReason}
            />
            {!pending.dueDate && (
              <Chip label={i18n.t("pendingDashboard.sinFechaChip")} size="small" className={classes.chipNoDate} />
            )}
            {hasNoAssignee && (
              <Chip label={i18n.t("pendingDashboard.sinResponsable")} size="small" className={classes.chipNoAssign} />
            )}
          </div>

          {/* Nota */}
          {pending.note && (
            <Typography className={classes.cardNote}>{pending.note}</Typography>
          )}

          {/* Footer */}
          <div className={classes.cardFooter}>
            <div className={classes.cardFooterLeft}>
              <Typography className={classes.cardAssigned}>
                {assignedName
                  ? i18n.t("pendingDashboard.asignadoA", { name: assignedName })
                  : i18n.t("pendingDashboard.sinAsignar")}
              </Typography>
              {createdByName && lastActionDate && (
                <Typography className={classes.cardLastAction}>
                  {i18n.t("pendingDashboard.ultimaAccion", { name: createdByName, date: lastActionDate })}
                </Typography>
              )}
            </div>
            <div className={classes.cardActions}>
              {/* Tomar: solo si no está asignado o no es mío */}
              {(hasNoAssignee || !isAssignedToMe) && (
                <Tooltip title={i18n.t("pendingDashboard.asignarmeEste")}>
                  <Button
                    size="small"
                    variant="outlined"
                    className={classes.btnAction}
                    startIcon={<PersonIcon style={{ fontSize: 13 }} />}
                    onClick={() => handleTake(pending)}
                  >
                    {i18n.t("pendingDashboard.tomar")}
                  </Button>
                </Tooltip>
              )}
              <Tooltip title={i18n.t("pendingDashboard.reprogramar")}>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.btnAction}
                  startIcon={<EventIcon style={{ fontSize: 13 }} />}
                  onClick={() => handleOpenReschedule(pending)}
                >
                  {i18n.t("pendingDashboard.reprogramar")}
                </Button>
              </Tooltip>
              <Tooltip title={i18n.t("pendingDashboard.marcarResuelto")}>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.btnAction}
                  startIcon={<CheckCircleIcon style={{ fontSize: 13, color: "#22c55e" }} />}
                  onClick={() => handleResolve(pending)}
                >
                  {i18n.t("pendingDashboard.resolver")}
                </Button>
              </Tooltip>
              <Tooltip title={i18n.t("pendingDashboard.irAlChat")}>
                <Button
                  size="small"
                  variant="contained"
                  className={classes.btnChat}
                  startIcon={<ChatIcon style={{ fontSize: 13 }} />}
                  onClick={() => handleGoToChat(pending)}
                >
                  {i18n.t("pendingDashboard.chat")}
                </Button>
              </Tooltip>
            </div>
          </div>
        </div>
      </Paper>
    );
  };

  // Render de un grupo
  const renderGroup = (key, label, color, items) => (
    <div className={classes.groupSection} key={key}>
      <div className={classes.groupHeader}>
        <div className={classes.groupDot} style={{ backgroundColor: color }} />
        <Typography className={classes.groupTitle}>{label}</Typography>
        <Typography className={classes.groupCount}>({items.length})</Typography>
      </div>
      {items.length === 0
        ? <Typography className={classes.emptyGroup}>{i18n.t("pendingDashboard.sinPendientesGrupo")}</Typography>
        : items.map(renderCard)
      }
    </div>
  );

  // RENDER PRINCIPAL
  return (
    <div className={classes.root}>

      {/* Título */}
      <div className={classes.pageTitle}>
        <AccessTimeIcon color="primary" />
        <Typography variant="h6" style={{ fontWeight: 500 }}>
          {i18n.t("pendingDashboard.titulo")}
        </Typography>
        <Tooltip title={i18n.t("pendingDashboard.actualizar")}>
          <IconButton size="small" onClick={loadPendings} style={{ marginLeft: 4 }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {!loading && (
          <Typography variant="caption" color="textSecondary" style={{ marginLeft: 4 }}>
            {i18n.t("pendingDashboard.actualizadoAt", { hora: new Date(lastRefresh).toLocaleTimeString(getLocale(), { hour: "2-digit", minute: "2-digit" }) })}
          </Typography>
        )}
      </div>

      {/* Métricas superiores */}
      <Grid container spacing={1} className={classes.metricsGrid}>
        {[
          { key: "mine", label: i18n.t("pendingDashboard.mios"), num: metrics.mine, cls: "numBlue" },
          { key: "overdue", label: i18n.t("pendingDashboard.vencidos"), num: metrics.overdue, cls: "numRed" },
          { key: "today", label: i18n.t("pendingDashboard.hoy"), num: metrics.today, cls: "numAmber" },
          { key: "upcoming", label: i18n.t("pendingDashboard.proximos7d"), num: metrics.upcoming, cls: "numBlue" },
          { key: "nodate", label: i18n.t("pendingDashboard.sinFecha"), num: metrics.nodate, cls: "numGray" },
          { key: "", label: i18n.t("pendingDashboard.totalActivos"), num: metrics.total, cls: "" },
        ].map(({ key, label, num, cls }) => (
          <Grid item xs={6} sm={4} md={2} key={key || "total"}>
            <Paper
              className={`${classes.metCard} ${filterMetric === key ? classes.metCardActive : ""}`}
              onClick={() => setFilterMetric(prev => prev === key ? "" : key)}
              variant="outlined"
            >
              <Typography className={`${classes.metNum} ${classes[cls] || ""}`}>{num}</Typography>
              <Typography className={classes.metLabel}>{label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filtros */}
      <div className={classes.filtersRow}>
        <FormControl size="small" variant="outlined" className={classes.filterControl}>
          <InputLabel>{i18n.t("pendingDashboard.ver")}</InputLabel>
          <Select
            value={viewMode}
            onChange={e => { setViewMode(e.target.value); setFilterUser(""); }}
            label={i18n.t("pendingDashboard.ver")}
          >
            <MenuItem value="mine">{i18n.t("pendingDashboard.soloMios")}</MenuItem>
            <MenuItem value="team">{i18n.t("pendingDashboard.miEquipo")}</MenuItem>
            {user.profile !== "user" && <MenuItem value="all">{i18n.t("pendingDashboard.todos")}</MenuItem>}
          </Select>
        </FormControl>

        {user.profile !== "user" && (
          <FormControl size="small" variant="outlined" className={classes.filterControl}>
            <InputLabel>{i18n.t("pendingDashboard.asesor")}</InputLabel>
            <Select
              value={filterUser}
              onChange={e => setFilterUser(e.target.value)}
              label={i18n.t("pendingDashboard.asesor")}
            >
              <MenuItem value="">{i18n.t("pendingDashboard.todos")}</MenuItem>
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl size="small" variant="outlined" className={classes.filterControl}>
          <InputLabel>{i18n.t("pendingDashboard.motivo")}</InputLabel>
          <Select
            value={filterReason}
            onChange={e => setFilterReason(e.target.value)}
            label={i18n.t("pendingDashboard.motivo")}
          >
            <MenuItem value="">{i18n.t("pendingDashboard.todos")}</MenuItem>
            {Object.entries(REASON_LABELS).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {(filterMetric || filterReason || filterUser) && (
          <Button
            size="small"
            onClick={() => { setFilterMetric(""); setFilterReason(""); setFilterUser(""); }}
          >
            {i18n.t("pendingDashboard.limpiarFiltros")}
          </Button>
        )}
      </div>

      {/* Contenido */}
      {loading ? (
        <div className={classes.loadingWrapper}>
          <CircularProgress size={32} />
        </div>
      ) : (
        <>
          {renderGroup("overdue", i18n.t("pendingDashboard.vencidos"), "#E24B4A", filteredGroups.overdue)}
          <Divider style={{ margin: "8px 0" }} />
          {renderGroup("today", i18n.t("pendingDashboard.hoy"), "#EF9F27", filteredGroups.today)}
          <Divider style={{ margin: "8px 0" }} />
          {renderGroup("upcoming", i18n.t("pendingDashboard.proximos7dias"), "#378ADD", filteredGroups.upcoming)}
          <Divider style={{ margin: "8px 0" }} />
          {renderGroup("nodate", i18n.t("pendingDashboard.sinFechaGrupo"), "#888780", filteredGroups.nodate)}
        </>
      )}

      {/* Modal de reprogramar */}
      <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n.t("pendingDashboard.reprogramarTitulo")}</DialogTitle>
        <DialogContent className={classes.modalContent}>
          <FormControl fullWidth variant="outlined" size="small">
            <InputLabel>¿Cuándo?</InputLabel>
            <Select
              value={reschedulePreset}
              onChange={e => {
                setReschedulePreset(e.target.value);
                setRescheduleShowCustom(e.target.value === "custom");
              }}
              label={i18n.t("pendingDashboard.cuando")}
            >
              {SNOOZE_PRESETS.filter(p => p.key !== "no_due_date").map(p => (
                <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {rescheduleShowCustom && (
            <TextField
              label={i18n.t("pendingDashboard.fechaYHora")}
              type="datetime-local"
              value={rescheduleCustomDate}
              onChange={e => setRescheduleCustomDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              variant="outlined"
              size="small"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRescheduleOpen(false)} disabled={rescheduleSaving}>
            {i18n.t("pendingDashboard.cancelar")}
          </Button>
          <Button
            onClick={handleSaveReschedule}
            color="primary"
            variant="contained"
            disabled={rescheduleSaving}
          >
            {rescheduleSaving ? <CircularProgress size={18} color="inherit" /> : i18n.t("pendingDashboard.guardar")}
          </Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default PendingDashboard;
